import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeftIcon, PhotoIcon, PlusIcon, PencilIcon, TrashIcon } from '@heroicons/react/24/outline';
import { sponsorsApi, Sponsor, SponsorCreate, SponsorUpdate, sponsorLogoSrc } from '../api/client';
import { getErrorMessage } from '../utils/errorHandler';

const MAX_LOGO_BYTES = 5 * 1024 * 1024;
const ALLOWED_LOGO_TYPES = ['image/png', 'image/jpeg'];

interface SponsorFormState {
  name: string;
  website_url: string;
  is_active: boolean;
  logo_data?: string;
  logo_content_type?: string;
  logo_filename?: string;
  remove_logo: boolean;
}

const emptyForm: SponsorFormState = {
  name: '',
  website_url: '',
  is_active: true,
  remove_logo: false,
};

const SponsorsPage: React.FC = () => {
  const navigate = useNavigate();
  const [sponsors, setSponsors] = useState<Sponsor[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<Sponsor | null>(null);
  const [form, setForm] = useState<SponsorFormState>(emptyForm);
  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    loadSponsors();
  }, []);

  const loadSponsors = async () => {
    try {
      const data = await sponsorsApi.listSponsors();
      setSponsors(data);
      setError(null);
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  const openCreate = () => {
    setEditing(null);
    setForm(emptyForm);
    setLogoPreview(null);
    setFormError(null);
    setModalOpen(true);
  };

  const openEdit = (sponsor: Sponsor) => {
    setEditing(sponsor);
    setForm({
      name: sponsor.name,
      website_url: sponsor.website_url || '',
      is_active: sponsor.is_active,
      remove_logo: false,
    });
    setLogoPreview(sponsor.has_logo ? sponsorLogoSrc(sponsor.logo_url) || null : null);
    setFormError(null);
    setModalOpen(true);
  };

  const closeModal = () => {
    if (saving) return;
    setModalOpen(false);
  };

  const handleLogoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (file.size > MAX_LOGO_BYTES) {
      setFormError('Logo file size must be less than 5MB');
      return;
    }
    if (!ALLOWED_LOGO_TYPES.includes(file.type)) {
      setFormError('Invalid file type. Please upload a PNG or JPEG image.');
      return;
    }

    const reader = new FileReader();
    reader.onload = (event) => {
      const dataUrl = event.target?.result as string;
      const base64Data = dataUrl.split(',')[1]; // Remove data:image/...;base64, prefix
      setForm((prev) => ({
        ...prev,
        logo_data: base64Data,
        logo_content_type: file.type,
        logo_filename: file.name,
        remove_logo: false,
      }));
      setLogoPreview(dataUrl);
      setFormError(null);
    };
    reader.readAsDataURL(file);
  };

  const removeLogo = () => {
    setForm((prev) => ({
      ...prev,
      logo_data: undefined,
      logo_content_type: undefined,
      logo_filename: undefined,
      remove_logo: true,
    }));
    setLogoPreview(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.name.trim()) {
      setFormError('Sponsor name is required');
      return;
    }
    setSaving(true);
    setFormError(null);
    try {
      const website = form.website_url.trim();
      if (editing) {
        const payload: SponsorUpdate = {
          name: form.name.trim(),
          website_url: website || null,
          is_active: form.is_active,
        };
        if (form.logo_data) {
          payload.logo_data = form.logo_data;
          payload.logo_content_type = form.logo_content_type;
          payload.logo_filename = form.logo_filename;
        } else if (form.remove_logo) {
          payload.remove_logo = true;
        }
        await sponsorsApi.updateSponsor(editing.id, payload);
      } else {
        const payload: SponsorCreate = {
          name: form.name.trim(),
          website_url: website || null,
          is_active: form.is_active,
        };
        if (form.logo_data) {
          payload.logo_data = form.logo_data;
          payload.logo_content_type = form.logo_content_type;
          payload.logo_filename = form.logo_filename;
        }
        await sponsorsApi.createSponsor(payload);
      }
      setModalOpen(false);
      await loadSponsors();
    } catch (err) {
      setFormError(getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (sponsor: Sponsor) => {
    const usage = sponsor.event_count > 0
      ? ` This will also remove it from ${sponsor.event_count} event${sponsor.event_count === 1 ? '' : 's'}.`
      : '';
    if (!window.confirm(`Delete sponsor "${sponsor.name}"?${usage}`)) return;
    try {
      await sponsorsApi.deleteSponsor(sponsor.id);
      setSponsors((prev) => prev.filter((s) => s.id !== sponsor.id));
    } catch (err) {
      setError(getErrorMessage(err));
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-5xl mx-auto px-4">
        <div className="mb-6">
          <button
            onClick={() => navigate('/dashboard')}
            className="flex items-center text-gray-600 hover:text-gray-900"
          >
            <ArrowLeftIcon className="h-5 w-5 mr-1" />
            Back to Dashboard
          </button>
        </div>

        <div className="bg-white rounded-lg shadow">
          <div className="p-6">
            <div className="flex justify-between items-start mb-6">
              <div>
                <h1 className="text-2xl font-bold">Sponsors</h1>
                <p className="text-sm text-gray-500 mt-1">
                  A library of sponsors you can attach to any event. Logos appear on the event page, badges, emails and social cards.
                </p>
              </div>
              <button
                onClick={openCreate}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors flex items-center gap-2 text-sm font-medium"
              >
                <PlusIcon className="h-4 w-4" />
                Add sponsor
              </button>
            </div>

            {error && (
              <div className="bg-red-50 border border-red-200 rounded p-3 text-red-700 mb-4">
                {error}
              </div>
            )}

            {sponsors.length === 0 ? (
              <div className="text-center py-12 text-gray-500">
                <PhotoIcon className="h-12 w-12 mx-auto text-gray-300 mb-3" />
                <p>No sponsors yet. Add your first sponsor to start using them on events.</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {sponsors.map((sponsor) => (
                  <div
                    key={sponsor.id}
                    className={`border rounded-lg p-4 flex flex-col ${sponsor.is_active ? 'border-gray-200' : 'border-gray-200 opacity-60'}`}
                  >
                    <div className="h-24 bg-white border border-gray-100 rounded-md flex items-center justify-center mb-3 p-3">
                      {sponsor.has_logo ? (
                        <img
                          src={sponsorLogoSrc(sponsor.logo_url)}
                          alt={sponsor.name}
                          className="max-h-full max-w-full object-contain"
                        />
                      ) : (
                        <PhotoIcon className="h-8 w-8 text-gray-300" />
                      )}
                    </div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <h3 className="font-medium text-gray-900">{sponsor.name}</h3>
                        {!sponsor.is_active && (
                          <span className="text-xs px-2 py-0.5 bg-gray-100 text-gray-600 rounded">Inactive</span>
                        )}
                      </div>
                      {sponsor.website_url && (
                        <a
                          href={sponsor.website_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-sm text-blue-600 hover:text-blue-800 break-all"
                        >
                          {sponsor.website_url.replace(/^https?:\/\//, '')}
                        </a>
                      )}
                      <p className="text-xs text-gray-500 mt-1">
                        Used on {sponsor.event_count} event{sponsor.event_count === 1 ? '' : 's'}
                      </p>
                    </div>
                    <div className="flex gap-2 mt-3 pt-3 border-t border-gray-100">
                      <button
                        onClick={() => openEdit(sponsor)}
                        className="flex-1 px-3 py-1.5 text-sm text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50 flex items-center justify-center gap-1"
                      >
                        <PencilIcon className="h-4 w-4" />
                        Edit
                      </button>
                      <button
                        onClick={() => handleDelete(sponsor)}
                        className="px-3 py-1.5 text-sm text-red-600 border border-red-200 rounded-md hover:bg-red-50 flex items-center justify-center gap-1"
                      >
                        <TrashIcon className="h-4 w-4" />
                        Delete
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {modalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg max-w-lg w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b flex justify-between items-center">
              <h2 className="text-xl font-semibold">{editing ? 'Edit sponsor' : 'Add sponsor'}</h2>
              <button onClick={closeModal} className="text-gray-500 hover:text-gray-700">✕</button>
            </div>
            <form onSubmit={handleSubmit} className="p-6 space-y-5">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Logo</label>
                <div className="flex items-start gap-6">
                  <div className="flex-shrink-0">
                    {logoPreview ? (
                      <div className="relative">
                        <img
                          src={logoPreview}
                          alt="Sponsor logo"
                          className="h-24 w-24 object-contain border border-gray-200 rounded-lg bg-white p-1"
                        />
                        <button
                          type="button"
                          onClick={removeLogo}
                          className="absolute -top-2 -right-2 bg-red-600 text-white rounded-full p-1 hover:bg-red-700"
                          title="Remove logo"
                        >
                          <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                          </svg>
                        </button>
                      </div>
                    ) : (
                      <div className="h-24 w-24 border-2 border-dashed border-gray-300 rounded-lg flex items-center justify-center">
                        <PhotoIcon className="h-8 w-8 text-gray-400" />
                      </div>
                    )}
                  </div>
                  <div className="flex-1">
                    <input
                      type="file"
                      accept="image/png,image/jpeg"
                      onChange={handleLogoChange}
                      className="block w-full text-sm text-gray-500
                        file:mr-4 file:py-2 file:px-4
                        file:rounded-md file:border-0
                        file:text-sm file:font-semibold
                        file:bg-blue-50 file:text-blue-700
                        hover:file:bg-blue-100"
                    />
                    <p className="mt-2 text-xs text-gray-500">
                      PNG or JPEG up to 5MB. A transparent PNG works best on badges and emails.
                    </p>
                  </div>
                </div>
              </div>

              <div>
                <label htmlFor="sponsor_name" className="block text-sm font-medium text-gray-700 mb-1">
                  Name
                </label>
                <input
                  id="sponsor_name"
                  type="text"
                  required
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <div>
                <label htmlFor="sponsor_website" className="block text-sm font-medium text-gray-700 mb-1">
                  Website URL (optional)
                </label>
                <input
                  id="sponsor_website"
                  type="url"
                  placeholder="https://"
                  value={form.website_url}
                  onChange={(e) => setForm({ ...form, website_url: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              <label className="flex items-center gap-2 text-sm text-gray-700">
                <input
                  type="checkbox"
                  checked={form.is_active}
                  onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
                  className="h-4 w-4 text-blue-600 border-gray-300 rounded"
                />
                Active (available to attach to events)
              </label>

              {formError && (
                <div className="bg-red-50 border border-red-200 rounded p-3 text-red-700 text-sm">
                  {formError}
                </div>
              )}

              <div className="flex justify-end gap-3 pt-2">
                <button
                  type="button"
                  onClick={closeModal}
                  disabled={saving}
                  className="px-4 py-2 text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50"
                >
                  {saving ? 'Saving…' : editing ? 'Save changes' : 'Add sponsor'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default SponsorsPage;
